import React, { useState, useEffect, useRef } from 'react';
import { withRouter } from "react-router-dom";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";

import CKEditor from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import EventList from '../../components/eventList/EventListComponent';
import EventService from '../../services/EventSettingsService';
import './EventSettingsComponent.scss';
import EventListDuc from '../../components/eventList/duc';


const DEFAULT_CURRENCY_CODE = 'USD';
const LATE_FEE_ERROR_MSG = 'Enter a positive integer (e.g., 500), or leave blank for manual entry.';
const DEFAULT_TIMEZONE = 'Asia/Kolkata';

// Some ICU builds enumerate Asia/Calcutta instead of Asia/Kolkata (the canonical
// IANA name since 2008). We replace known legacy names so the dropdown always
// shows the modern canonical name. Intl.DateTimeFormat accepts both on all
// environments, so conversions are unaffected.
const LEGACY_TO_CANONICAL = { 'Asia/Calcutta': 'Asia/Kolkata' };

// Built at module load — runs once. Falls back to IST-only if Intl API is unavailable.
const IANA_TIMEZONES = (() => {
    try {
        const now = new Date();
        return Intl.supportedValuesOf('timeZone')
            .map(tz => {
                const canonical = LEGACY_TO_CANONICAL[tz] || tz;
                const abbr = new Intl.DateTimeFormat('en-IN', { timeZone: tz, timeZoneName: 'short' })
                    .formatToParts(now).find(p => p.type === 'timeZoneName').value;
                const offset = new Intl.DateTimeFormat('en', { timeZone: tz, timeZoneName: 'shortOffset' })
                    .formatToParts(now).find(p => p.type === 'timeZoneName').value
                    .replace('GMT', 'UTC');
                return { value: canonical, label: `${canonical} — ${abbr} (${offset})` };
            })
            .sort((a, b) => a.value.localeCompare(b.value));
    } catch (e) {
        return [{ value: DEFAULT_TIMEZONE, label: 'Asia/Kolkata — IST (UTC+5:30)' }];
    }
})();

const getTimezoneSample = (tz) => {
    try {
        const now = new Date();
        const parts = new Intl.DateTimeFormat('en-GB', {
            timeZone: tz,
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit', hour12: false
        }).formatToParts(now).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
        const abbr = new Intl.DateTimeFormat('en-IN', { timeZone: tz, timeZoneName: 'short' })
            .formatToParts(now).find(p => p.type === 'timeZoneName').value;
        return `${parts.day} ${parts.month} ${parts.year}, ${parts.hour}:${parts.minute} ${abbr}`;
    } catch (e) {
        return '';
    }
};

const EventSettings = (props) => {
    const [eventData, setEventData] = useState();
    const [validated, setValidated] = useState(false);
    const [bulkImportTC, setBulkImportTC] = useState('');
    const [pointsToRemember, setPointsToRemember] = useState('');
    const [userMessage, setUserMessage] = useState({isError: false, message: ''});
    const [isRequired, setRequiredAttribute] = useState(false);
    const aspectRatioSettingsRef = useRef(null);
    const [showPageTitleHelp, setShowPageTitleHelp] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);

    // Timezone
    const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);

    // Late Fee Configuration
    const [currencyList, setCurrencyList] = useState([]);
    const [isLatePolicyEnabled, setIsLatePolicyEnabled] = useState(false);
    const [currencyCode, setCurrencyCode] = useState(DEFAULT_CURRENCY_CODE);
    const [defaultLateFee, setDefaultLateFee] = useState('');
    const [lateFeeError, setLateFeeError] = useState('');
    // event.isCurrencyCodeLocked === true ⇒ Currency dropdown is disabled (locked).
    // Set by the first policy-active ACK export and never resettable thereafter.
    // The locked value is simply event.currencyCode (single source of truth).
    const [isCurrencyCodeLocked, setIsCurrencyCodeLocked] = useState(false);

    useEffect(() => {
        EventService.getCurrencyList()
            .then((res) => setCurrencyList(res?.data?.data?.result || []))
            .catch(() => setCurrencyList([]));
    }, []);

    useEffect(() => {
        EventService.getEventSettings(props?.selectedEvent).then((res) => {
            const data = res?.data?.data;
            if (data) {
                setEventData(data);
                setTimezone(data.timezone || DEFAULT_TIMEZONE);
                setIsLatePolicyEnabled(Boolean(data.enableLateFormSubmission));
                setIsCurrencyCodeLocked(Boolean(data.isCurrencyCodeLocked));
                // currencyCode is the single source of truth (immutable once locked),
                // so the dropdown always reflects it directly.
                setCurrencyCode(data.currencyCode || DEFAULT_CURRENCY_CODE);
                setDefaultLateFee(
                    data.defaultLateFeePerCard === null || data.defaultLateFeePerCard === undefined
                        ? ''
                        : String(data.defaultLateFeePerCard)
                );
                setLateFeeError('');
            }
            if (aspectRatioSettingsRef.current) {
                handleRequiredAttribute(aspectRatioSettingsRef.current.checked);
            }
            setShowPageTitleHelp(false);
        });
    }, [props]);

    const validateLateFee = (raw) => {
        const value = (raw || '').trim();
        if (value === '') return '';
        // Positive integer only: reject 0, negatives, decimals, non-numeric.
        if (!/^[1-9][0-9]*$/.test(value)) return LATE_FEE_ERROR_MSG;
        return '';
    };

    const handleLateFeeChange = (e) => {
        const value = e.target.value;
        setDefaultLateFee(value);
        setLateFeeError(validateLateFee(value));
    };

    const handleLatePolicyToggle = (e) => {
        setIsLatePolicyEnabled(e.target.checked);
    };

    const handleTimezoneChange = (e) => {
        setTimezone(e.target.value);
    };


    const handleSubmit = (e)=>{
        const form = e.currentTarget;
        e.preventDefault();
        e.stopPropagation();
        setValidated(true);
        if(form.checkValidity() === false){
            setUserMessage({
                isError: true, message: "Please fix the form errors!"
            })
        } else if (form.isImageAspectRatioMandatory.checked
                && (!(+form.imageHeight.value>0) || !(+form.imageWidth.value>0))) {
            setUserMessage({
                isError: true, message: "Please enter valid Photo Aspect Ratio Settings!"
            });
        } else if (lateFeeError) {
            setUserMessage({
                isError: true, message: LATE_FEE_ERROR_MSG
            });
        } else {
            setUserMessage({isError: false, message: ""})
            const formData = {
                eventId: props.selectedEvent,
                timezone: timezone,
                nameCharacterLimit: +form.nameCharacterLimit.value,
                firstNameCharacterLimit: +form.firstNameCharacterLimit.value,
                lastNameCharacterLimit: +form.lastNameCharacterLimit.value,
                mobileNumberCharacterLimit: +form.mobileNumberCharacterLimit.value,
                userCompanyCharacterLimit: +form.userCompanyCharacterLimit.value,
                eventDayDutyNameCharacterLimit: +form.eventDayDutyNameCharacterLimit.value,
                barcodeCharacterLimit: +form.barcodeCharacterLimit.value,
                ageLimit: +form.ageLimit.value,
                playoffStatus: form.playoffStatus.value,
                otrApplicable: form.otrApplicable.checked,
                serialApplicable: form.serialApplicable.checked,
                barcodeApplicable: form.barcodeApplicable.checked,
                showAccessAreaAsCheckbox: form.showAccessAreaAsCheckbox.checked,
                bibNumberApplicable: form.bibNumberApplicable.checked,
                emailCentralVendorMgr: form.emailCentralVendorMgr.checked,
                negativeCardStock: form.negativeCardStock.checked,
                confirmIdCardNo: form.confirmIdCardNo.checked,
                enableLateFormSubmission: form.enableLateFormSubmission.checked,
                currencyCode: currencyCode || DEFAULT_CURRENCY_CODE,
                defaultLateFeePerCard: defaultLateFee.trim() === '' ? null : parseInt(defaultLateFee, 10),
                imageDimensions: [],
                isImageAspectRatioMandatory: form.isImageAspectRatioMandatory.checked,
                is2faApplicable: form.is2faApplicable.checked,
                accessAreaSeparatorForPrint: form.accessAreaSeparatorForPrint.value,
                accessAreaPlaceholderForPrint: form.accessAreaPlaceholderForPrint.value,
                fileUploadSizeMaxLimit: +form.fileUploadSizeMaxLimit.value,
                showApplyingParty: form.showApplyingParty.checked,
                bulkImportConfirmTnC: bulkImportTC,
                pointsToRemember: pointsToRemember,
                pageTitle: form.pageTitle.value,
                copyrightText: form.copyrightText.value,
                isSetAccessAreaEnabled: form.isSetAccessAreaEnabled.checked,
                excludeSavedFormsFromLimit: eventData?.isSgpEvent ? false : form.excludeSavedFormsFromLimit.checked
            }

            if (+form.imageHeight.value>0 && +form.imageWidth.value>0) {
                formData.imageDimensions = [
                    {
                        photo: {
                            height: +form.imageHeight.value,
                            width: +form.imageWidth.value,
                        }
                    }
                ];
            }

            setDisableSubmit(true);
            const config = {
                headers: {
                    'eventurl': eventData.eventUrl
                }
            }
            EventService.updateEventSettings(formData, config).then(res => {
                setUserMessage({isError: false, message: "Changes have been updated."});
                setDisableSubmit(false);
            }, err =>{
                setUserMessage({isError: true, message: err?.response?.data?.message});
                setDisableSubmit(false);
            })
        }
    }

    const handlePhotoAspectRatioSettings = () => {
        const isChecked = aspectRatioSettingsRef.current.checked;
        handleRequiredAttribute(isChecked);
    }

    const handleRequiredAttribute = (booleanValue) => {
        setRequiredAttribute(booleanValue);
        setValidated(true);
    }

    const handlePageTitleChange = (e) => {
        setShowPageTitleHelp(e.target.value !== eventData.pageTitle);
    }

    // A single text/number input field. `width` maps to the responsive column
    // helper class (3 => third row, 6 => half row, 12 => full row).
    const inputField = ({ name, label, type = 'number', required = true, width = 4 }) => (
        <div className={`field field--${width}`}>
            <label htmlFor={name}>{label}</label>
            <input
                id={name}
                name={name}
                type={type}
                {...(type === 'number' ? { min: '0' } : {})}
                required={required}
                key={eventData?.[name]}
                defaultValue={eventData?.[name]}
            />
            {required && <div className="invalid-msg">This field is required.</div>}
        </div>
    );

    // A single checkbox field.
    const checkField = ({ name, label, width = 4, inputRef, onClick }) => (
        <div className={`field field--${width}`}>
            <label className="check">
                <input
                    id={name}
                    name={name}
                    type="checkbox"
                    key={eventData?.[name]}
                    defaultChecked={eventData?.[name]}
                    ref={inputRef}
                    onClick={onClick}
                />
                <span>{label}</span>
            </label>
        </div>
    );

    return (<div className="es-container">
        <div className="panel event-list">
            <h5>Event</h5>
            <EventList />
        </div>

        <form
            className={`es-form${validated ? ' was-validated' : ''}`}
            onSubmit={handleSubmit}
            noValidate
            key={props?.selectedEvent}
        >
            <div className="panel">
                <h4>Accreditation Form</h4>

                <div className="field-row">
                    {inputField({ name: 'nameCharacterLimit', label: 'Set Character Limit For Complete Name*' })}
                    {inputField({ name: 'firstNameCharacterLimit', label: 'Set Character Limit For First Name*' })}
                    {inputField({ name: 'lastNameCharacterLimit', label: 'Set Character Limit For Last Name*' })}
                </div>

                <div className="field-row">
                    {inputField({ name: 'mobileNumberCharacterLimit', label: 'Set Character Limit For Mobile Number*' })}
                    {inputField({ name: 'userCompanyCharacterLimit', label: 'Set Character Limit For User Company*' })}
                    {inputField({ name: 'eventDayDutyNameCharacterLimit', label: 'Set Character Limit For Event Day Duty Name*', type: 'text' })}
                </div>

                <div className="field-row">
                    {inputField({ name: 'barcodeCharacterLimit', label: 'Barcode Length*' })}
                    {inputField({ name: 'ageLimit', label: 'Min Age for Event*' })}

                    <div className="field field--4">
                        <label htmlFor="playoffStatus">Set Playoff Status*</label>
                        <select id="playoffStatus" name="playoffStatus" key={eventData?.playoffStatus} defaultValue={eventData?.playoffStatus}>
                            <option value="Pending">Pending</option>
                            <option value="Approved">Approved</option>
                            <option value="Rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                <div className="divider"> </div>

                <div className="field-row">
                    {checkField({ name: 'otrApplicable', label: 'One Time Registeration Form' })}
                    {checkField({ name: 'serialApplicable', label: 'Serial Number Applicable' })}
                    {checkField({ name: 'barcodeApplicable', label: 'Barcode Manual Upload' })}
                    {checkField({ name: 'showAccessAreaAsCheckbox', label: 'Show Access Area as Checkbox' })}
                </div>

                <div className="field-row">
                    {checkField({ name: 'bibNumberApplicable', label: 'Bib Number Applicable' })}
                    {checkField({ name: 'emailCentralVendorMgr', label: 'Enable Central Vendor Manager Email' })}
                </div>

                <div className="field-row">
                    {checkField({ name: 'negativeCardStock', label: 'Allow Negative Card Stock' })}
                    {checkField({ name: 'confirmIdCardNo', label: 'Confirm ID Card Number' })}
                    {checkField({
                        name: 'enableLateFormSubmission',
                        label: 'Enable Late Form Submission',
                        onClick: handleLatePolicyToggle,
                    })}
                </div>

                {isLatePolicyEnabled && (
                    <div className="late-fee-block">
                        <h5 className="late-fee-block__title">LATE FEE CONFIGURATION</h5>
                        <div className="field-row">
                            <div className="field field--6">
                                <label htmlFor="currencyCode">Currency<span className="required-mark">*</span></label>
                                <select
                                    id="currencyCode"
                                    name="currencyCode"
                                    value={currencyCode}
                                    onChange={(e) => setCurrencyCode(e.target.value)}
                                    disabled={Boolean(isCurrencyCodeLocked)}
                                    required
                                >
                                    {currencyList.length === 0 && (
                                        <option value={DEFAULT_CURRENCY_CODE}>$ - USD - US Dollar</option>
                                    )}
                                    {currencyList.map((c) => (
                                        <option key={c.code} value={c.code}>
                                            {`${c.symbol} - ${c.code} - ${c.name}`}
                                        </option>
                                    ))}
                                </select>
                                {isCurrencyCodeLocked && (
                                    <small className="field-help">
                                        Currency is locked because acknowledgement forms have already been generated for this event with this currency.
                                    </small>
                                )}
                            </div>

                            <div className="field field--6">
                                <label htmlFor="defaultLateFeePerCard">
                                    Default Late Fee per card <span className="optional-mark">(optional)</span>
                                </label>
                                <input
                                    id="defaultLateFeePerCard"
                                    name="defaultLateFeePerCard"
                                    type="text"
                                    inputMode="numeric"
                                    placeholder="e.g., 500"
                                    value={defaultLateFee}
                                    onChange={handleLateFeeChange}
                                    className={lateFeeError ? 'has-error' : ''}
                                />
                                {lateFeeError && <div className="field-error">{lateFeeError}</div>}
                                {!lateFeeError && defaultLateFee.trim() === '' && (
                                    <small className="field-help">
                                        Leave this blank to skip auto-calculation — the operator will enter the late fee manually on the Acknowledgement Form, as today.
                                    </small>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div className="panel">
                <h4>Timezone Settings</h4>
                <div className="field-row">
                    <div className="field field--6">
                        <label htmlFor="timezone">Timezone<span className="required-mark">*</span></label>
                        <select
                            id="timezone"
                            name="timezone"
                            value={timezone}
                            onChange={handleTimezoneChange}
                            required
                        >
                            {IANA_TIMEZONES.map(tz => (
                                <option key={tz.value} value={tz.value}>{tz.label}</option>
                            ))}
                        </select>
                        <small className="field-help">Drives every timestamp accred renders for this event.</small>
                    </div>
                    <div className="field field--6">
                        <label>Sample timestamp, as this event's users will read it</label>
                        <input
                            type="text"
                            className="field-readonly"
                            value={getTimezoneSample(timezone)}
                            readOnly
                        />
                        <small className="field-help">The zone abbreviation is shown beside the time — a reader who cannot tell whether 14:30 is local or IST cannot act on it.</small>
                    </div>
                </div>
            </div>

            <div className="panel">
                <h4>Photo Aspect Ratio Settings</h4>
                <div className="field-row">
                    <div className="field field--6">
                        <label htmlFor="imageHeight">Height{isRequired && (<>*</>)}</label>
                        <input type="number" min="0" id="imageHeight" name="imageHeight" required={isRequired} key={eventData?.imageDimensions[0]?.photo?.height} defaultValue={eventData?.imageDimensions[0]?.photo?.height} />
                        <div className="invalid-msg">This field is required.</div>
                    </div>

                    <div className="field field--6">
                        <label htmlFor="imageWidth">Width{isRequired && (<>*</>)}</label>
                        <input type="text" id="imageWidth" name="imageWidth" required={isRequired} key={eventData?.imageDimensions[0]?.photo?.width} defaultValue={eventData?.imageDimensions[0]?.photo?.width} />
                        <div className="invalid-msg">This field is required.</div>
                    </div>
                </div>

                <div className="field-row">
                    {checkField({
                        name: 'isImageAspectRatioMandatory',
                        label: 'Photo Aspect Ratio Mandatory (Disable Form Submission)',
                        width: 12,
                        inputRef: aspectRatioSettingsRef,
                        onClick: handlePhotoAspectRatioSettings,
                    })}
                </div>
            </div>

            <div className="panel">
                <h4>Authentication Settings</h4>
                <div className="field-row">
                    {checkField({ name: 'is2faApplicable', label: '2 Factor Authentication Applicable' })}
                </div>
            </div>

            <div className="panel">
                <h4>Print Module</h4>
                <div className="field-row">
                    {inputField({ name: 'accessAreaSeparatorForPrint', label: 'Set Access Area Separator Character For Print*', type: 'text', width: 6 })}
                    {inputField({ name: 'accessAreaPlaceholderForPrint', label: 'Set Access Area Placeholder For Print(If Empty)', type: 'text', required: false, width: 6 })}
                </div>
            </div>

            <div className="panel">
                <h4>Bulk Import</h4>
                <div className="field-row">
                    {inputField({ name: 'fileUploadSizeMaxLimit', label: 'Set Maximum Limit for File Upload Size(MB)*', width: 6 })}
                    <div className="field field--4 show-applying-party">
                        <label className="check">
                            <input id="showApplyingParty" name="showApplyingParty" type="checkbox" key={eventData?.showApplyingParty} defaultChecked={eventData?.showApplyingParty} />
                            <span>Show Applying Party</span>
                        </label>
                    </div>
                </div>

                <section>
                    <h6>Bulk Import Terms &amp; Conditions - Confirm</h6>
                    <div className="ck-editor-group">
                        <CKEditor
                            config={
                                {
                                link: {
                                    addTargetToExternalLinks: true
                                    }
                                }
                            }
                            editor={ClassicEditor}
                            data={eventData?.bulkImportConfirmTnC || ''}
                            onInit={editor => {
                                setBulkImportTC(eventData?.bulkImportConfirmTnC);
                            }}
                            onChange={(event, editor) => {
                                const data = editor.getData();
                                setBulkImportTC(data);
                                }}
                        />
                    </div>
                </section>

                <section>
                    <h6>Points To Remember</h6>
                    <div className="ck-editor-group">
                        <CKEditor
                            config={
                                {
                                link: {
                                    addTargetToExternalLinks: true
                                    }
                                }
                            }
                            editor={ClassicEditor}
                            data={eventData?.pointsToRemember || ''}
                            onInit={editor => {
                                setPointsToRemember(eventData?.pointsToRemember);
                            }}
                            onChange={(event, editor) => {
                                const data = editor.getData();
                                setPointsToRemember(data);
                                }}
                        />
                    </div>
                </section>
            </div>

            <div className="panel">
                <h4>Page Settings</h4>
                <div className="field-row">
                    <div className="field field--6">
                        <label htmlFor="pageTitle">Page Title</label>
                        <input type="text" id="pageTitle" name="pageTitle" key={eventData?.pageTitle} defaultValue={eventData?.pageTitle} onChange={handlePageTitleChange} />
                        {showPageTitleHelp &&
                            <small className="field-help">
                                Updated text will take effect once the user(s) log out and log in again.
                            </small>
                        }
                    </div>
                </div>
                <div className="field-row">
                    <div className="field field--12">
                        <label htmlFor="copyrightText">Copyright Text</label>
                        <input type="text" id="copyrightText" name="copyrightText" key={eventData?.copyrightText} defaultValue={eventData?.copyrightText} />
                    </div>
                </div>
            </div>

            <div className="panel">
                <h4>Approvals</h4>
                <div className="field-row">
                    {checkField({ name: 'isSetAccessAreaEnabled', label: 'Set Access Area', width: 6 })}
                </div>
            </div>

            {/* Checkbox to Form Limit Calculation: exclude Saved Forms. Show only if SGP is false */}
            {!eventData?.isSgpEvent && (
                <div className="panel">
                    <h4>Form Limit Calculation</h4>
                    <div className="field-row">
                        {checkField({ name: 'excludeSavedFormsFromLimit', label: 'Exclude Saved Forms', width: 6 })}
                    </div>
                </div>
            )}

            {/* Show success or error message in the alert */}
            {userMessage?.message && (
                <div className="alert-row">
                    <div className={`alert-msg ${userMessage?.isError ? 'alert-msg--error' : 'alert-msg--success'}`}>
                        {userMessage?.message}
                    </div>
                </div>
            )}

            <div className="submit-btn">
                <button type="submit" className="btn-submit" disabled={disableSubmit || Boolean(lateFeeError)}>
                    Submit
                </button>
            </div>
        </form>
    </div>)
}

const mapDispatchToProps = () => ({});

export default connect(
    createStructuredSelector({
        selectedEvent: EventListDuc?.selectors?.selectedEvent,
    }),
    mapDispatchToProps,
)(withRouter(EventSettings));